/**
 * Compares a client heartbeat version with the newest published stable and beta builds.
 * The release lists come from PandaUnion GET /api/admin/client-latest-releases:
 *   desktop (win32/darwin V2Panda): CLIENT_VERSION, plain semver such as 3.5.1-beta.3
 *   ios / tvos (BaoLianDeng): TestFlight groups; the heartbeat sends <marketing>+<build>
 *   android / openwrt (also Merlin and koolcenter): release channels; the heartbeat sends <name>+<versionCode>
 */

export type LatestReleaseSource = 'desktop' | 'ios' | 'tvos' | 'android' | 'openwrt'

/** `order` is a semver string for desktop and a build number for the other clients. */
export type LatestRelease = { label: string; order: string | number }
export type LatestReleases = { stable: LatestRelease | null; beta: LatestRelease | null }

export type ClientVersionVerdict = {
  status: 'latest' | 'latest-stable' | 'outdated' | 'unknown'
  text: string
}

type Heartbeat = { version?: string; platform?: string }

export const latestReleaseSourceFor = (heartbeat: Heartbeat): LatestReleaseSource | null => {
  switch (heartbeat.platform) {
    case 'win32':
    case 'darwin':
      return 'desktop'
    case 'ios':
    case 'ipados':
      return 'ios'
    case 'tvos':
    case 'android':
    case 'openwrt':
      return heartbeat.platform
    default:
      return null
  }
}

const newer = (a: LatestRelease | null, b: LatestRelease | null) => {
  if (!a || !b) return a ?? b
  return compareOrder(b.order, a.order) > 0 ? b : a
}

const SEMVER = /^v?(\d+)\.(\d+)\.(\d+)(?:-([0-9A-Za-z.-]+))?$/

const compareSemver = (left: string, right: string) => {
  const a = SEMVER.exec(left)
  const b = SEMVER.exec(right)
  if (!a || !b) return null
  for (const index of [1, 2, 3]) {
    const difference = Number(a[index]) - Number(b[index])
    if (difference) return difference
  }
  const preA = a[4]?.split('.') ?? []
  const preB = b[4]?.split('.') ?? []
  if (!preA.length || !preB.length) return preB.length - preA.length
  for (let index = 0; index < Math.max(preA.length, preB.length); index += 1) {
    const partA = preA[index]
    const partB = preB[index]
    if (partA === undefined) return -1
    if (partB === undefined) return 1
    if (partA === partB) continue
    const numeric = /^\d+$/.test(partA) && /^\d+$/.test(partB)
    return numeric ? Number(partA) - Number(partB) : partA.localeCompare(partB)
  }
  return 0
}

function compareOrder(left: string | number, right: string | number): number {
  if (typeof left === 'number' && typeof right === 'number') return left - right
  const result = compareSemver(String(left), String(right))
  if (result === null) throw new Error(`cannot compare versions ${left} and ${right}`)
  return result
}

const clientOrder = (heartbeat: Heartbeat): string | number | null => {
  const version = heartbeat.version?.trim() ?? ''
  if (latestReleaseSourceFor(heartbeat) === 'desktop') return SEMVER.test(version) ? version : null
  const code = /\+(\d+)$/.exec(version)?.[1]
  return code ? Number(code) : null
}

export const describeClientVersion = (heartbeat: Heartbeat, releases: LatestReleases): ClientVersionVerdict => {
  const current = clientOrder(heartbeat)
  const newest = newer(releases.stable, releases.beta)
  if (current === null || !newest) return { status: 'unknown', text: '' }

  const { stable, beta } = releases
  if (compareOrder(current, newest.order) >= 0) return { status: 'latest', text: '已是最新' }
  if (stable && beta && compareOrder(current, stable.order) >= 0) {
    return { status: 'latest-stable', text: `已是最新正式版，测试版 ${beta.label}` }
  }
  const choices = [stable && `正式版 ${stable.label}`, beta && (!stable || compareOrder(beta.order, stable.order) > 0) && `测试版 ${beta.label}`].filter(Boolean)
  return { status: 'outdated', text: `可升级：${choices.join('，')}` }
}
